import React, { useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRight, MapPin, Sparkles, Wrench, Bell, ShieldCheck } from 'lucide-react';
import useSeo from '../hooks/useSeo';
import FaqSection from '../components/FaqSection';
import { getCity, CITIES } from '../data/cityCleaning';

/**
 * Local SEO landing page: "Airbnb / short let cleaning and changeovers in
 * [city]". Driven by src/data/cityCleaning.js. Honest pre-launch framing — we
 * are building the local cleaning network and inviting hosts to list and cleaners
 * to join, not claiming supply we do not yet have. Unknown slugs redirect to
 * the hub so we never serve an empty/duplicate page.
 */

// FAQ template; {{CITY}} is replaced with the city name at render time so each
// city page gets its own visible FAQ and matching FAQPage JSON-LD.
const CITY_FAQ = [
  { question: "How much does Airbnb and short let cleaning cost in {{CITY}}?", answer: "Changeover cleaning prices in {{CITY}} depend on the size of the property, the turnaround time and whether linen and laundry are included. Ariv is building its local cleaner network across {{CITY}} now, so list your place and we will line up clear, upfront changeover pricing as local coverage grows." },
  { question: "Do you have cleaners available in {{CITY}} right now?", answer: "We are honest that Ariv is pre launch and building its local cleaner and trade network in {{CITY}} city by city, so we are matching the first hosts and cleaners as coverage goes live rather than claiming a finished service. List your place at /become-host to get matched early, or if you clean professionally join at /provider/signup." },
  { question: "What is a changeover clean and what does it include?", answer: "A changeover clean is the full turnaround between guests: a complete clean of the property, fresh linen and towels, a bathroom and kitchen reset and a restock of basics so the place is guest ready for the next arrival. As coverage goes live, Ariv lines up local {{CITY}} cleaners around each booking so back to back stays do not turn into a scramble." },
  { question: "How do I become an Airbnb cleaner in {{CITY}}?", answer: "If you clean short stays or holiday lets in {{CITY}}, you can join Ariv at /provider/signup to get in early on local changeover work as the network grows. As the network launches we will ID and background check the cleaners and tradespeople, so hosts know who is coming to their property." },
  { question: "Is Ariv cheaper than a traditional Airbnb management company in {{CITY}}?", answer: "Traditional UK Airbnb managers typically take 15 to 30 percent of your revenue, while Ariv charges a 13 percent host commission, host only so the guest pays no separate fee, below Airbnb's 15 to 18 percent. The first 100 founding hosts pay 0 percent for their first 90 days, then 10 percent for the rest of year one. You stay in control of your {{CITY}} listing and can run Ariv alongside the platforms you already use, with no lock in." },
];

const fillCity = (text, name) => text.replace(/\{\{CITY\}\}/g, name);

const Section = ({ title, children }) => (
  <section className="max-w-3xl mx-auto px-6 py-8 border-t border-gray-100">
    <h2 className="text-2xl font-bold text-[#1F3A68] mb-3">{title}</h2>
    <div className="text-gray-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

const LocalCleaningPage = () => {
  const { city: slug } = useParams();
  const city = getCity(slug);

  // Hooks must run unconditionally, so compute everything with safe fallbacks
  // and only redirect after.
  const faq = useMemo(
    () =>
      city
        ? CITY_FAQ.map((f) => ({
            question: fillCity(f.question, city.name),
            answer: fillCity(f.answer, city.name),
          }))
        : [],
    [city]
  );

  const jsonLd = useMemo(() => {
    if (!city) return [];
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `Airbnb and short let cleaning in ${city.name}`,
        serviceType: "Short let changeover cleaning",
        provider: { "@type": "Organization", name: "Ariv", legalName: "Brick & Capital Ltd", url: "https://ariv.co.uk/" },
        areaServed: { "@type": "City", name: city.name },
        url: `https://ariv.co.uk/airbnb-cleaning/${city.slug}/`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://ariv.co.uk/" },
          { "@type": "ListItem", position: 2, name: "Airbnb cleaning", item: "https://ariv.co.uk/airbnb-cleaning/" },
          { "@type": "ListItem", position: 3, name: city.name, item: `https://ariv.co.uk/airbnb-cleaning/${city.slug}/` },
        ],
      },
    ];
  }, [city]);

  useSeo({
    title: city
      ? `Airbnb & short let cleaning in ${city.name} | Ariv for hosts`
      : 'Airbnb & short let cleaning | Ariv for hosts',
    description: city
      ? `Building a local cleaning and changeover network for short stay hosts in ${city.name}. List your place to get matched, or join Ariv as a local cleaner or tradesperson.`
      : 'Local cleaning and changeovers for UK short stay hosts.',
    canonical: city ? `https://ariv.co.uk/airbnb-cleaning/${city.slug}/` : undefined,
    image: 'https://ariv.co.uk/icons/icon-512.png',
    imageAlt: 'Ariv',
    faq,
    jsonLd,
  });

  if (!city) return <Navigate to="/airbnb-cleaning/" replace />;

  const otherCities = CITIES.filter((c) => c.slug !== city.slug);

  return (
    <div className="min-h-screen bg-white">
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></Link>
          <Link to="/become-host" className="bg-[#1F3A68] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90">List your place</Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 pt-4 text-sm text-gray-500">
        <Link to="/" className="hover:text-[#1F3A68]">Home</Link>
        <span className="mx-1">/</span>
        <Link to="/airbnb-cleaning/" className="hover:text-[#1F3A68]">Airbnb cleaning</Link>
        <span className="mx-1">/</span>
        <span className="text-gray-700">{city.name}</span>
      </div>

      <header className="bg-gradient-to-br from-[#F4F2E9] to-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <p className="inline-flex items-center gap-1 text-xs font-semibold text-[#1F3A68] bg-[#1F3A68]/10 px-3 py-1 rounded-full mb-4">
            <MapPin size={13} /> {city.name}, {city.region}
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F3A68] mb-4 leading-tight">
            Airbnb and short let cleaning in {city.name}
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">{city.intro}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <Link to="/become-host" className="inline-flex items-center justify-center gap-2 bg-[#1F3A68] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1F3A68]/90">
              List your place <ArrowRight size={18} />
            </Link>
            <Link to="/provider/signup" className="inline-flex items-center justify-center gap-2 border-2 border-[#1F3A68] text-[#1F3A68] px-6 py-3 rounded-lg font-semibold hover:bg-[#1F3A68]/5">
              Join as a cleaner or tradesperson
            </Link>
          </div>
        </div>
      </header>

      <Section title={`Building Ariv's local network across ${city.name}`}>
        <p>
          Ariv is rolling out its local cleaner and trade network city by city, and {city.name} is one we are building now. We are honest about where we are: rather than promising a finished service, we are matching the first hosts and local cleaners as the network goes live.
        </p>
        <p>
          If you host a short stay in {city.name}, list your place and we will line up changeover cleaning and trades around your bookings as coverage grows. If you are a cleaner or tradesperson, this is the moment to get in early on local work.
        </p>
      </Section>

      <Section title={`What Ariv covers for ${city.name} hosts`}>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: Sparkles, t: 'Changeover cleaning', d: 'Local cleaners lined up around each booking, so back to back stays do not turn into a scramble.' },
            { icon: Wrench, t: 'Trades when something breaks', d: 'A trusted local tradesperson on tap, so a broken boiler does not become a cancelled stay.' },
            { icon: Bell, t: 'Stock and restock alerts', d: 'Live alerts when linen, coffee, or toiletries run low at a property, before a guest notices.' },
            { icon: ShieldCheck, t: 'People you can see, not anyone', d: 'You see who is coming to your property. As the network launches we will ID and background check cleaners and trades, so it is not just whoever was cheapest that week.' },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="border border-gray-200 rounded-xl p-5">
              <Icon size={22} className="text-[#1F3A68] mb-2" />
              <p className="font-bold text-gray-900 mb-1">{t}</p>
              <p className="text-sm text-gray-600">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Where in ${city.name} we are starting`}>
        <p>We are focusing first on the areas with the most short stay activity, including:</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {city.areas.map((a) => (
            <span key={a} className="text-sm bg-[#F4F2E9] text-[#1F3A68] px-3 py-1 rounded-full">{a}</span>
          ))}
        </div>
        <p className="mt-3 text-sm text-gray-600">Busiest stretch locally: {city.peak}</p>
      </Section>

      <Section title={city.localNote.title}>
        <p>{city.localNote.body}</p>
      </Section>

      <Section title={`Get matched in ${city.name}`}>
        <p>
          Listing takes about fifteen minutes and there is no lock in, so you can run Ariv alongside the platforms you already use while the local network grows.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <Link to="/become-host" className="inline-flex items-center justify-center gap-2 bg-[#1F3A68] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#1F3A68]/90">
            List your place on Ariv <ArrowRight size={18} />
          </Link>
          <Link to="/provider/signup" className="inline-flex items-center justify-center gap-2 border-2 border-[#1F3A68] text-[#1F3A68] px-6 py-3 rounded-lg font-semibold hover:bg-[#1F3A68]/5">
            Join as a local provider
          </Link>
        </div>
      </Section>

      <Section title="Other cities">
        <div className="flex flex-wrap gap-2">
          {otherCities.map((c) => (
            <Link key={c.slug} to={`/airbnb-cleaning/${c.slug}/`} className="text-sm bg-[#F4F2E9] text-[#1F3A68] px-3 py-1 rounded-full hover:bg-[#1F3A68]/10">
              {c.name}
            </Link>
          ))}
        </div>
      </Section>

      <FaqSection faq={faq} title={`Airbnb cleaning in ${city.name}: questions`} />

      <footer className="bg-[#1F3A68] text-white py-8 mt-8">
        <div className="max-w-3xl mx-auto px-6 text-center text-white/70 text-sm">
          <p>&copy; {new Date().getFullYear()} Ariv. A product of Brick &amp; Capital Ltd.</p>
          <p className="text-xs mt-2 text-white/50">Registered in England and Wales, company number 16655788. Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX.</p>
          <div className="flex flex-wrap gap-4 justify-center mt-3">
            <Link to="/become-host" className="hover:text-white">List your place</Link>
            <Link to="/low-commission-airbnb-management/" className="hover:text-white">Low commission management</Link>
            <Link to="/trust" className="hover:text-white">Trust &amp; Safety</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LocalCleaningPage;
